import { ArrowUpRight } from 'lucide-react'
import { type BillingInterval, type SubscriptionTier } from '@/lib/api'
import { useRouter } from '@/routing/navigation'
import { PlanCard, type PlanCardCta } from '@/components/billing/PlanCard'
import { BILLING_LOCKED_MESSAGE } from '@/components/billing/BillingLockedTooltip'
import { CONTACT_SALES_URL, PLAN_CARDS } from './planConfig'
import { usePlanActions } from './usePlanActions'
import { useTranslation } from 'react-i18next'

export interface PlanCardsRowProps {
  currentTier: SubscriptionTier
  /** Accepted for call-site compatibility; drives behavior again with Paddle. */
  hasActiveSubscription: boolean
  /** Accepted for call-site compatibility; the interval toggle is retired. */
  currentInterval: BillingInterval | null | undefined
  /** Accepted for call-site compatibility; no seat-capped plan is sold now. */
  memberCount: number
  isOwner?: boolean
}

export function PlanCardsRow(props: PlanCardsRowProps) {
  const { currentTier } = props
  const { t } = useTranslation('app')

  const router = useRouter()

  const actions = usePlanActions({ currentTier })

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 min-h-[36px]">
        <button
          type="button"
          onClick={() => router.push('/settings/billing/plans')}
          data-testid="billing-learn-more-link"
          className="inline-flex items-center gap-1 text-sm font-medium text-foreground underline-offset-2 hover:underline"
        >
          {t('billing.comparePlans')}
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {PLAN_CARDS.map((config) => {
          const tier = config.tier
          const cta: PlanCardCta = tier ? actions.ctaForPlan(tier) : { kind: 'contact-sales' }
          return (
            <PlanCard
              key={config.id}
              config={config}
              currentTier={currentTier}
              cta={cta}
              onCta={actions.handleCta}
              ctaDisabledReason={tier ? BILLING_LOCKED_MESSAGE : undefined}
              downgradeHint={tier ? actions.downgradeHint(tier) : null}
              className="items-start"
              contentClassName="w-full sm:max-w-[300px]"
              ctaClassName="w-full"
              testIdPrefix="billing"
            />
          )
        })}
      </div>
      <p className="text-center text-sm text-muted-foreground">
        {t('billing.needCustomTerms')}{' '}
        <a
          href={CONTACT_SALES_URL}
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-2 hover:text-foreground transition-colors"
          data-testid="billing-talk-to-sales"
        >
          {t('billing.talkToSales')}
        </a>
      </p>
    </div>
  )
}
